// src/components/common/widgets/CheckoutExpiringAlert.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconClock, IconCopy, IconCheck, IconX, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { thunkFetchExpiringCouponsForClient } from '../../../redux/slices/couponSlice';

const CheckoutExpiringAlert = ({ onCouponApply }) => {
  const dispatch = useDispatch();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [countdown, setCountdown] = useState('');

  const { clientExpiringCoupons, clientLoading } = useSelector((state) => state.coupon);

  // Fetch expiring coupons when component mounts
  useEffect(() => {
    dispatch(thunkFetchExpiringCouponsForClient());
  }, [dispatch]);

  // Find coupon expiring within 7 days (instead of 24 hours)
  const urgentCoupon = clientExpiringCoupons?.find(coupon => {
    const timeLeft = new Date(coupon.end_date) - new Date();
    const hoursLeft = timeLeft / (1000 * 60 * 60);
    return hoursLeft <= 168 && hoursLeft > 0; // 168 hours = 7 days
  });

  // Countdown timer effect
  useEffect(() => {
    if (!urgentCoupon) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const end = new Date(urgentCoupon.end_date).getTime();
      const distance = end - now;

      if (distance < 0) {
        setCountdown('EXPIRED');
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setCountdown(`${days}d ${hours}h`);
      } else {
        setCountdown(`${hours}h ${minutes}m`);
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 60000); // Update every minute
    return () => clearInterval(timer);
  }, [urgentCoupon]);

  const handleApply = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      onCouponApply?.(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  // Don't show if dismissed, loading, or no urgent coupon
  if (isDismissed || clientLoading || !urgentCoupon) {
    return null;
  }

  const timeLeft = new Date(urgentCoupon.end_date) - new Date();
  const hoursLeft = timeLeft / (1000 * 60 * 60);
  const daysLeft = Math.floor(hoursLeft / 24);
  
  // Determine urgency level
  let urgencyLevel = 'moderate';
  let urgencyColor = 'from-blue-600 to-blue-500';
  let urgencyIcon = '💰';
  let urgencyText = 'SAVE NOW';
  
  if (hoursLeft <= 24) {
    urgencyLevel = 'urgent';
    urgencyColor = 'from-red-600 to-red-500';
    urgencyIcon = '🔥';
    urgencyText = 'URGENT';
  } else if (hoursLeft <= 72) {
    urgencyLevel = 'warning';
    urgencyColor = 'from-orange-600 to-orange-500';
    urgencyIcon = '⚡';
    urgencyText = 'HURRY';
  }

  return (
    <div className={`fixed top-1/2 left-0 transform -translate-y-1/2 z-50 transition-all duration-300 ${
      isCollapsed ? '-translate-x-full' : 'translate-x-0'
    }`}>
      {/* Collapsed Tab */}
      {isCollapsed && (
        <div 
          onClick={() => setIsCollapsed(false)}
          className={`absolute -right-12 top-1/2 transform -translate-y-1/2 bg-gradient-to-l ${urgencyColor} 
                     text-white px-2 py-4 rounded-r-lg cursor-pointer hover:shadow-lg transition-all
                     writing-mode-vertical text-center shadow-lg`}
        >
          <div className="flex flex-col items-center space-y-1">
            <IconChevronRight size={16} />
            <span className="text-xs font-bold transform rotate-90 whitespace-nowrap">
              {urgencyIcon} {urgentCoupon.coupon_type === 'percentage' 
                ? `${urgentCoupon.discount_value}%` 
                : `${urgentCoupon.discount_value}`} OFF
            </span>
          </div>
        </div>
      )}

      {/* Main Alert Panel */}
      <div className={`bg-gradient-to-l ${urgencyColor} text-white shadow-2xl rounded-r-lg 
                     border-r-4 border-white/30 max-w-sm w-80 transform transition-all duration-300`}>
        
        {/* Header with collapse/close buttons */}
        <div className="flex items-center justify-between p-3 border-b border-white/20">
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setIsCollapsed(true)}
              className="hover:bg-white/20 p-1 rounded transition-colors"
              title="Minimize"
            >
              <IconChevronLeft size={16} />
            </button>
           
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-lg">{urgencyIcon}</span>
            <span className="text-xs font-bold bg-white/20 px-2 py-1 rounded">
              {urgencyText}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <div className="text-center">
            <div className="text-lg font-bold">
              {urgentCoupon.coupon_type === 'percentage' 
                ? `${urgentCoupon.discount_value}% OFF` 
                : `$${urgentCoupon.discount_value} OFF`}
            </div>
            <div className="text-sm text-white/80">
              {urgentCoupon.name}
            </div>
          </div>

          <div className="flex items-center justify-center space-x-2 text-sm">
            <IconClock size={16} />
            <span>Expires in {countdown}</span>
          </div>

          <button
            onClick={() => handleApply(urgentCoupon.code)}
            className="w-full bg-white/20 hover:bg-white/30 py-2 px-4 rounded-lg text-sm font-medium 
                     transition-all flex items-center justify-center space-x-2"
          >
            {copiedCode === urgentCoupon.code ? (
              <>
                <IconCheck size={16} />
                <span>Applied!</span>
              </>
            ) : (
              <>
                <IconCopy size={16} />
                <span>Copy Code: {urgentCoupon.code}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutExpiringAlert;